import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Play, Pause, X, Volume2, VolumeX, Loader2, AlertTriangle, Star, Radio } from "lucide-react";
import { streamUrl, logoUrl } from "../lib/api";

function EqBars({ active }) {
  const bars = [0.15, 0.35, 0.05, 0.25, 0.45, 0.2, 0.3];
  return (
    <div className="flex items-end gap-[3px] h-8" data-testid="eq-visualizer">
      {bars.map((d, i) => (
        <span
          key={i}
          className="w-[3px] rounded-full rv-gradient eq-bar"
          style={{ height: "100%", animationDelay: `${d}s`, animationPlayState: active ? "running" : "paused", opacity: active ? 1 : 0.3 }}
        />
      ))}
    </div>
  );
}

export default function AudioPlayer({ station, onClose, isFav, onFav }) {
  const audioRef = useRef(null);
  const hlsRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | playing | paused | error
  const [reason, setReason] = useState("");
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(() => Number(localStorage.getItem("radioverse_volume") || 0.8));

  useEffect(() => {
    if (!station) return;
    const audio = audioRef.current;
    const src = streamUrl(station.url);
    setStatus("loading");
    setReason("");

    const cleanup = () => {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
    cleanup();

    const onPlaying = () => setStatus("playing");
    const onPause = () => setStatus((s) => (s === "error" ? s : "paused"));
    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("pause", onPause);
    audio.volume = volume;

    const isM3U8 = /\.(m3u8)(\?|$)/i.test(station.url);

    if (isM3U8 && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(audio);
      hls.on(Hls.Events.MANIFEST_PARSED, () => audio.play().catch(() => {}));
      hls.on(Hls.Events.ERROR, (_e, data) => {
        const code = data?.response?.code;
        if (code >= 400) {
          let detail = "";
          try { detail = JSON.parse(data.response.text || "{}").detail || ""; } catch {}
          setReason(detail); setStatus("error"); cleanup(); return;
        }
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
          else { setStatus("error"); cleanup(); }
        }
      });
    } else {
      audio.src = src;
      audio.play().catch(() => {});
      audio.onerror = () => { setStatus("error"); };
    }

    const timeout = setTimeout(() => setStatus((s) => (s === "loading" ? "error" : s)), 15000);

    return () => {
      clearTimeout(timeout);
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("pause", onPause);
      audio.onerror = null;
      cleanup();
    };
  }, [station]); // eslint-disable-line

  const togglePlay = () => {
    const a = audioRef.current;
    if (a.paused) { a.play().catch(() => {}); } else { a.pause(); }
  };
  const toggleMute = () => {
    const a = audioRef.current;
    a.muted = !a.muted;
    setMuted(a.muted);
  };
  const changeVolume = (v) => {
    setVolume(v);
    localStorage.setItem("radioverse_volume", String(v));
    if (audioRef.current) audioRef.current.volume = v;
  };

  if (!station) return null;
  const active = status === "playing";

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 ml-0 md:ml-[220px] border-t border-rv-border bg-rv-medium/95 backdrop-blur-xl animate-fadeIn" data-testid="audio-player-bar">
      <audio ref={audioRef} autoPlay data-testid="audio-element" />
      <div className="flex items-center gap-4 px-4 sm:px-6 py-3">
        {/* Station info */}
        <div className="flex items-center gap-3 min-w-0 w-1/3">
          <div className="w-12 h-12 rounded-xl bg-rv-card border border-rv-border flex items-center justify-center overflow-hidden shrink-0">
            {station.logo ? (
              <img src={logoUrl(station.logo)} alt="" className="max-w-[80%] max-h-[80%] object-contain" onError={(e) => (e.target.style.display = "none")} />
            ) : (
              <Radio size={20} className="text-rv-muted" />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-bold text-rv-text truncate flex items-center gap-2" title={station.name}>
              <span className="w-2 h-2 rounded-full bg-rv-red animate-pulseDot shrink-0" />
              {station.name}
            </div>
            <div className="text-xs text-rv-muted truncate">
              {station.genre}{station.country ? ` · ${station.country}` : ""}{station.bitrate ? ` · ${station.bitrate}kbps` : ""}
            </div>
          </div>
        </div>

        {/* Transport + visualizer */}
        <div className="flex items-center gap-4 flex-1 justify-center">
          <button onClick={togglePlay} disabled={status === "error"} className="w-12 h-12 rounded-full rv-gradient flex items-center justify-center text-white shadow-lg disabled:opacity-50 hover:brightness-110 transition" data-testid="player-playpause-button">
            {status === "loading" ? <Loader2 size={22} className="animate-spin" /> :
             status === "playing" ? <Pause size={22} fill="currentColor" /> :
             <Play size={22} fill="currentColor" className="ml-0.5" />}
          </button>
          <div className="hidden sm:block">
            {status === "error" ? (
              <div className="flex items-center gap-2 text-rv-orange text-xs max-w-xs" data-testid="player-error">
                <AlertTriangle size={15} className="shrink-0" />
                <span className="truncate">{reason || "This station could not be played. Try another."}</span>
              </div>
            ) : (
              <EqBars active={active} />
            )}
          </div>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-2 sm:gap-3 justify-end w-1/3">
          <button onClick={() => onFav(station)} className={`btn-ghost p-2.5 ${isFav ? "!text-rv-yellow !border-rv-yellow/40" : ""}`} title="Favourite" data-testid="player-favorite-button">
            <Star size={16} fill={isFav ? "currentColor" : "none"} />
          </button>
          <button onClick={toggleMute} className="btn-ghost p-2.5" data-testid="player-mute-button">
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <input
            type="range" min={0} max={1} step={0.01} value={volume}
            onChange={(e) => changeVolume(Number(e.target.value))}
            className="hidden md:block w-24 accent-rv-violet cursor-pointer"
            data-testid="player-volume-slider"
          />
          <button onClick={onClose} className="btn-ghost p-2.5 hover:!text-rv-red hover:!border-rv-red/40" data-testid="player-close-button">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
